import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, PartyPopper, Flame } from 'lucide-react';
import { useSurveyTabAutoClose } from '../../hooks/useSurveyTabClose';

export default function SurveyCompletePage() {
  const [params] = useSearchParams();
  const points = params.get('points') ?? '0';
  // The API redirect sets pending=0 when THIS credit posted instantly (trusted
  // member), 1 (or absent) when it's in the 30-day hold. Keyed on the actual
  // credit, never on current trust level — so we never falsely claim "instant".
  const instantCredit = params.get('pending') === '0';
  const [countdown, setCountdown] = useState(8);
  const { showCloseButton } = useSurveyTabAutoClose({ type: 'complete', points: Number(points) }, 5000, false);

  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          window.location.href = '/dashboard';
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4 }}
        className="bg-white rounded-2xl shadow-lg max-w-md w-full p-8 text-center space-y-6"
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
        >
          <CheckCircle2 className="w-20 h-20 text-[#10B981] mx-auto" strokeWidth={1.5} />
        </motion.div>

        <h1 className="text-2xl font-bold font-display text-[#111827] inline-flex items-center justify-center gap-2">
          Survey Complete! <PartyPopper className="w-6 h-6 text-[#5B4FE8]" />
        </h1>

        <div className="inline-flex items-center gap-2 bg-[#10B981]/10 text-[#10B981] font-bold text-lg px-5 py-2.5 rounded-full">
          +{points} pts
        </div>

        {instantCredit ? (
          <p className="text-sm text-[#10B981] font-medium">
            Nice work — because you're a trusted member, your {points} points are available
            right away, no waiting.
          </p>
        ) : (
          <p className="text-sm text-[#6B7280]">
            Your {points} points will clear over the next 30 days as the buyer confirms — it
            protects everyone from fraud.
          </p>
        )}

        <p className="text-sm text-[#F97316] font-medium inline-flex items-center justify-center gap-1.5">
          <Flame className="w-4 h-4" /> Streak kept alive!
        </p>

        <div className="flex flex-col sm:flex-row gap-3 pt-2">
          <Link
            to="/surveys"
            className="flex-1 py-3 rounded-full bg-[#5B4FE8] text-white text-sm font-medium hover:bg-[#4338CA] transition-colors text-center"
          >
            Take Another Survey
          </Link>
          <Link
            to="/dashboard"
            className="flex-1 py-3 rounded-full border-2 border-[#E5E7EB] text-[#111827] text-sm font-medium hover:bg-gray-50 transition-colors text-center"
          >
            Go to Dashboard
          </Link>
        </div>

        {showCloseButton ? (
          <button
            onClick={() => window.close()}
            className="text-sm text-[#5B4FE8] font-medium hover:underline"
          >
            Close this tab
          </button>
        ) : (
          <p className="text-xs text-[#9CA3AF]">
            Redirecting to dashboard in {countdown}s...
          </p>
        )}
      </motion.div>
    </div>
  );
}
